import i18n from '../../../core/i18n';
import services from '../../../core/services';
import { IAlertsService, INotificationsService } from '../../../platform/services/types';
import { BulkActionService } from '../../common/services/bulkActionService';
import filesApi, { MoveObject } from '../api/files';
import puiFileUpdateNotification from '../components/files/FileUpdateNotification.vue';
import {
  FileTaskStatus,
  FileTaskTypes,
  MoveStates,
  UpdateAttributeStates,
} from '../constants/file';
import { getDamService } from '.';
import { IFileTasksService, TaskManager } from './types';

export class FileUpdateTaskData {
  public updateAttrStatus = UpdateAttributeStates.IN_PROGRESS;
  public moveStatus = MoveStates.IN_PROGRESS;

  /**
   * @returns merged state of the two operations
   */
  public state(): FileTaskStatus {
    if (
      this.updateAttrStatus === UpdateAttributeStates.IN_PROGRESS ||
      this.moveStatus === MoveStates.IN_PROGRESS
    ) {
      return FileTaskStatus.IN_PROGRESS;
    } else if (
      this.updateAttrStatus === UpdateAttributeStates.ERROR ||
      this.moveStatus === MoveStates.ERROR
    ) {
      return FileTaskStatus.ERROR;
    }
    return FileTaskStatus.DONE_SUCCESS;
  }
}

export class FileUpdateManager {
  /**
   * ID of the notification
   */
  notificationId: number;
  taskManager: TaskManager<FileUpdateTaskData> | undefined = undefined;

  constructor(files: string[]) {
    this.notificationId = -1;
    this.taskManager = getDamService<IFileTasksService>('fileTasks')?.addTask(
      files,
      FileTaskTypes.UPDATE,
      new FileUpdateTaskData()
    );
  }

  /**
   * Updates the files' attributes, and classifications at the same time
   *
   * @param update - object describing the attribute update
   * @param update.fileIds - ids of the files whose attributes to update
   * @param update.attributes - object array with all attribute id and value
   * @param associate - array of files/folders pairs to associate
   * @param dissociate - array of files/folders pairs to dissociate
   * @param updateTaskName - the task to execute for the file update
   * @param moveTaskName - the task to execute for the file move
   */
  static async updateFiles(
    update: {
      fileIds: string[];
      attributes: any[];
    },
    associate: MoveObject[],
    dissociate: MoveObject[],
    updateTaskName: string | null,
    moveTaskName: string | null
  ): Promise<void> {
    let fileUpdateManager;

    try {
      const allFiles = [
        ...new Set([
          ...update.fileIds,
          ...associate.flatMap(a => a.fileIds),
          ...dissociate.flatMap(a => a.fileIds),
        ]),
      ];

      fileUpdateManager = new FileUpdateManager(allFiles);
      await fileUpdateManager.init();

      await Promise.all([
        (async () => {
          try {
            if (!update.fileIds.length || !update.attributes.length) {
              fileUpdateManager.onUpdateAttrsFinished();
              return;
            }

            const createUpdateRes = await filesApi.updateFiles(
              update.fileIds,
              update.attributes,
              updateTaskName
            );

            BulkActionService.pollBulkAction(createUpdateRes.body.bulkActionId, {
              onStatusFinished: fileUpdateManager.onUpdateAttrsFinished.bind(fileUpdateManager),
              onStatusInError: fileUpdateManager.onUpdateAttrsFailed.bind(fileUpdateManager),
            });
          } catch (err) {
            fileUpdateManager.onUpdateAttrsFailed();
          }
        })(),
        (async () => {
          try {
            if (!associate.length && !dissociate.length) {
              fileUpdateManager.onMoveFinished();
              return;
            }

            const createMoveFilesRes = await filesApi.moveFiles(
              associate,
              dissociate,
              moveTaskName
            );

            BulkActionService.pollBulkAction(createMoveFilesRes.body.bulkActionId, {
              onStatusFinished: fileUpdateManager.onMoveFinished.bind(fileUpdateManager),
              onStatusInError: fileUpdateManager.onMoveFailed.bind(fileUpdateManager),
            });
          } catch (err) {
            fileUpdateManager.onMoveFailed();
          }
        })(),
      ]);
    } catch (err) {
      if (fileUpdateManager) {
        fileUpdateManager.onProcessCrashed();
      }
    }
  }

  /**
   * Create the initial notification for an update
   */
  async init(): Promise<void> {
    this.notificationId =
      (await services
        .getService<INotificationsService>('notifications')
        ?.createNotification(puiFileUpdateNotification, this.taskManager?.data)) ?? -1;
    services.getService<INotificationsService>('notifications')?.openNotifications();
  }

  /**
   * Updates the task's state and creates an error alert
   */
  onProcessCrashed(): void {
    this.taskManager?.update(data => {
      data.updateAttrStatus = UpdateAttributeStates.ERROR;
      data.moveStatus = MoveStates.ERROR;
    });
    this.taskManager?.removeTask();

    services
      .getService<IAlertsService>('alerts')
      ?.alertError(i18n.t('dam.files.update.alert.error') as string);
  }

  /**
   * Creates the final success/error alert
   */
  checkProcessEnded(): void {
    if (
      this.taskManager?.data.updateAttrStatus === UpdateAttributeStates.IN_PROGRESS ||
      this.taskManager?.data.moveStatus === MoveStates.IN_PROGRESS
    ) {
      // Wait until both are done
      return;
    }

    if (
      this.taskManager?.data.updateAttrStatus === UpdateAttributeStates.ERROR ||
      this.taskManager?.data.moveStatus === MoveStates.ERROR
    ) {
      services
        .getService<IAlertsService>('alerts')
        ?.alertError(i18n.t('dam.files.update.alert.error') as string);
    } else {
      services
        .getService<IAlertsService>('alerts')
        ?.alertSuccess(i18n.t('dam.files.update.alert.success') as string);
    }

    this.taskManager?.removeTask();
  }

  /**
   * Updates the task's state when a files update has succeeded
   *
   * @returns {void}
   */
  onUpdateAttrsFinished(): void {
    this.taskManager?.update(data => {
      data.updateAttrStatus = UpdateAttributeStates.DONE_SUCCESS;
    });

    this.checkProcessEnded();
  }

  /**
   * Updates the task's state when a files update failed
   */
  onUpdateAttrsFailed(): void {
    this.taskManager?.update(data => {
      data.updateAttrStatus = UpdateAttributeStates.ERROR;
    });

    this.checkProcessEnded();
  }

  /**
   * Updates the task's state when the files move succeeded
   */
  onMoveFinished(): void {
    this.taskManager?.update(data => {
      data.moveStatus = MoveStates.DONE_SUCCESS;
    });

    this.checkProcessEnded();
  }

  /**
   * Updates the task's state when the files move failed
   */
  onMoveFailed(): void {
    this.taskManager?.update(data => {
      data.moveStatus = MoveStates.ERROR;
    });

    this.checkProcessEnded();
  }
}
